import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { INgxsFormModel } from '@pro-connect/shared';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { concatMap, Observable, throwError, timeout } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { IEventTicketFormModel } from './models/event-ticket-form-model';
import { IEventTicketDetails } from './models/event-ticket-details';
import { EventTicketService } from './event-ticket.service';
import { IEventTicketDto } from './models/event-ticket-dto';

export class EventTicketStateRegister {
  public static readonly type = '[EventTicketState] register';

  public constructor(
    public readonly payload: {
      code: string;
    },
  ) {}
}

export class EventTicketStateGetDetails {
  public static readonly type = '[EventTicketState] get details';

  public constructor(
    public readonly payload: {
      code: string;
    },
  ) {}
}

export interface EventTicketStateModel {
  loading: boolean;
  form: INgxsFormModel<IEventTicketFormModel>;
  errorMessage: string | null;
  eventDetails: IEventTicketDetails | null;
}

@State<EventTicketStateModel>({
  name: 'eventTicket',
  defaults: {
    loading: false,
    form: {
      model: {
        firstname: '',
        lastname: '',
        email: '',
        mobile: '',
        agreementTraining: false,
        agreementSms: false,
        agreementEmail: false,
        agreementPhone: false,
        agreementWhatsApp: false,
      },
      errors: {},
      dirty: false,
      disabled: false,
      status: 'pristine',
    },
    errorMessage: null,
    eventDetails: null,
  },
})
@Injectable()
export class EventTicketState {
  public constructor(
    private readonly _eventTicketService: EventTicketService,
    private readonly _recaptchaV3Service: ReCaptchaV3Service,
  ) {}

  @Selector()
  public static loading({ loading }: EventTicketStateModel): boolean {
    return loading;
  }

  @Selector()
  public static errorMessage({ errorMessage }: EventTicketStateModel): string | null {
    return errorMessage;
  }

  @Selector()
  public static eventDetails({ eventDetails }: EventTicketStateModel): IEventTicketDetails | null {
    return eventDetails;
  }

  @Action(EventTicketStateGetDetails)
  public getDetails(
    { patchState }: StateContext<EventTicketStateModel>,
    { payload: { code } }: EventTicketStateGetDetails,
  ): Observable<IEventTicketDetails> {
    patchState({ loading: true, errorMessage: null });
    return this._recaptchaV3Service.execute('importantAction').pipe(
      timeout({ first: 5000, with: () => throwError(() => 'timeout') }),
      concatMap((recaptchaKey: string) => this._eventTicketService.getDetails(code, recaptchaKey)),
      catchError((error) => throwError(() => error)),
      tap((eventDetails: IEventTicketDetails) => {
        patchState({ eventDetails });
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }

  @Action(EventTicketStateRegister)
  public register(
    { getState, patchState }: StateContext<EventTicketStateModel>,
    { payload: { code } }: EventTicketStateRegister,
  ): Observable<unknown> {
    const {
      form: {
        model: {
          firstname,
          lastname,
          email,
          mobile,
          agreementTraining,
          agreementSms,
          agreementEmail,
          agreementPhone,
          agreementWhatsApp,
        },
      },
    } = getState();
    if (!firstname || !lastname || !email || !mobile || !agreementTraining) {
      return throwError(() => 'Form is invalid');
    }

    const dto: IEventTicketDto = {
      firstname,
      lastname,
      email,
      mobile: `48${mobile}`,
      agreementTraining: 1,
      agreementSms: agreementSms ? 1 : 0,
      agreementEmail: agreementEmail ? 1 : 0,
      agreementPhone: agreementPhone ? 1 : 0,
      agreementWhatsApp: agreementWhatsApp ? 1 : 0,
    };

    patchState({ loading: true, errorMessage: null });
    return this._recaptchaV3Service.execute('importantAction').pipe(
      timeout({ first: 5000, with: () => throwError(() => 'timeout') }),
      concatMap((recaptchaKey: string) => this._eventTicketService.register(code, recaptchaKey, dto)),
      catchError((error) => throwError(() => error)),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }
}
